/**
 * Node Collision Resolution Utility
 * Based on xyflow's collision detection algorithm
 * https://github.com/xyflow/node-collision-algorithms
 */

/**
 * Creates bounding boxes from React Flow nodes
 * @param {Array} nodes - Array of React Flow nodes
 * @param {number} margin - Margin to add around each node
 * @returns {Array} Array of box objects
 */
function getBoxesFromNodes(nodes, margin = 0) {
  return nodes.map((node) => {
    // Use measured dimensions if available, otherwise use defaults
    const width = (node.measured?.width ?? node.width ?? 320) + margin * 2;
    const height = (node.measured?.height ?? node.height ?? 200) + margin * 2;
    
    return {
      x: node.position.x - margin,
      y: node.position.y - margin,
      width,
      height,
      moved: false,
      node,
    };
  });
}

/**
 * Resolves overlapping nodes by moving them apart
 * Uses a naive O(n²) algorithm that iteratively separates colliding nodes
 * 
 * @param {Array} nodes - Array of React Flow nodes
 * @param {Object} options - Resolution options
 * @param {number} options.maxIterations - Maximum iterations (default: 50)
 * @param {number} options.overlapThreshold - Minimum overlap to trigger resolution (default: 0.5)
 * @param {number} options.margin - Margin between nodes (default: 20)
 * @returns {Object} { nodes: resolvedNodes, numIterations }
 */
export function resolveCollisions(nodes, options = {}) {
  const {
    maxIterations = 50,
    overlapThreshold = 0.5,
    margin = 20,
  } = options;

  if (!nodes || nodes.length === 0) {
    return { nodes: [], numIterations: 0 };
  }

  const boxes = getBoxesFromNodes(nodes, margin);
  let numIterations = 0;

  for (let iter = 0; iter <= maxIterations; iter++) {
    let moved = false;

    // Check all pairs for collisions (O(n²))
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const A = boxes[i];
        const B = boxes[j];

        // Calculate center positions
        const centerAX = A.x + A.width * 0.5;
        const centerAY = A.y + A.height * 0.5;
        const centerBX = B.x + B.width * 0.5;
        const centerBY = B.y + B.height * 0.5;

        // Calculate distance between centers
        const dx = centerAX - centerBX;
        const dy = centerAY - centerBY;

        // Calculate overlap along each axis
        const px = (A.width + B.width) * 0.5 - Math.abs(dx);
        const py = (A.height + B.height) * 0.5 - Math.abs(dy);

        // Check if there's significant overlap
        if (px > overlapThreshold && py > overlapThreshold) {
          A.moved = B.moved = moved = true;

          // Resolve along the smallest overlap axis
          if (px < py) {
            // Move along x-axis
            const sx = dx > 0 ? 1 : -1;
            const moveAmount = (px / 2) * sx;
            A.x += moveAmount;
            B.x -= moveAmount;
          } else {
            // Move along y-axis
            const sy = dy > 0 ? 1 : -1;
            const moveAmount = (py / 2) * sy;
            A.y += moveAmount;
            B.y -= moveAmount;
          }
        }
      }
    }

    numIterations++;

    // Early exit if no overlaps were found
    if (!moved) {
      break;
    }
  }

  // Convert boxes back to nodes with updated positions
  const resolvedNodes = boxes.map((box) => {
    if (box.moved) {
      return {
        ...box.node,
        position: {
          x: box.x + margin,
          y: box.y + margin,
        },
      };
    }
    return box.node;
  });

  return { nodes: resolvedNodes, numIterations };
}

export default resolveCollisions;
